import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import PageSEO from "@/components/feature/PageSEO";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://example.com";

const modelsJsonLd = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "name": "Airdog Air Purifier Models - Complete X-Series Lineup",
  "description": "Explore every Airdog model: X3, X5, X8, Pet P50, and V5 Car. Compare coverage, specs, and find the perfect TPA-powered air purifier for any space.",
  "url": `${siteUrl}/models`,
  "isPartOf": {
    "@type": "WebSite",
    "name": "Airdog",
    "url": siteUrl,
  },
};

const models = [
  {
    model: "Airdog X3",
    coverage: "860 sq. ft.",
    desc: "Compact & whisper-quiet — ideal for bedrooms and home offices. Perfect for smaller spaces that still demand medical-grade purification.",
    color: "bg-sky-50",
    border: "border-sky-100",
    icon: "ri-home-smile-line",
    iconColor: "text-sky-600",
    iconBg: "bg-sky-100",
  },
  {
    model: "Airdog X5",
    coverage: "1,600 sq. ft.",
    desc: "The sweet spot for most homes. Our best-selling model balances power and efficiency for complete family protection.",
    badge: "Best Seller",
    color: "bg-brand/5",
    border: "border-brand/15",
    icon: "ri-home-4-line",
    iconColor: "text-brand",
    iconBg: "bg-brand/10",
  },
  {
    model: "Airdog X8",
    coverage: "4,000 sq. ft.",
    desc: "Built for open-concept living — cleans 1,000 sq. ft. in 15 minutes. Maximum power for large homes and commercial spaces.",
    badge: "Most Powerful",
    color: "bg-amber-50",
    border: "border-amber-100",
    icon: "ri-building-line",
    iconColor: "text-amber-600",
    iconBg: "bg-amber-100",
  },
  {
    model: "Airdog Pet (P50)",
    coverage: "Up to 1,600 sq. ft.",
    desc: "Tuned for pet dander and hair. Enhanced filtration for animal households with the same washable TPA technology.",
    tag: "Pet Friendly",
    color: "bg-emerald-50",
    border: "border-emerald-100",
    icon: "ri-footprint-line",
    iconColor: "text-emerald-600",
    iconBg: "bg-emerald-100",
  },
  {
    model: "Airdog V5 (Car)",
    coverage: "10–50 sq. ft.",
    desc: "Portable 12V / USB purifier for cars, RVs, and travel. Take medical-grade air with you wherever you drive.",
    tag: "Portable",
    color: "bg-violet-50",
    border: "border-violet-100",
    icon: "ri-car-line",
    iconColor: "text-violet-600",
    iconBg: "bg-violet-100",
  },
];

export default function ModelsPage() {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <PageSEO jsonLd={modelsJsonLd} />
      <Header />

      <main className="flex-1 pt-12">
        {/* Home button — top left */}
        <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl pt-2 md:pt-3 flex justify-start">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-gray-200 hover:border-brand/40 hover:bg-brand/5 transition-all duration-300 cursor-pointer group shadow-sm"
            aria-label="Back to Home"
          >
            <div className="w-4 h-4 rounded-full bg-brand/10 flex items-center justify-center group-hover:bg-brand/20 transition-colors">
              <i className="ri-arrow-left-line text-brand text-[10px]" />
            </div>
            <span className="text-xs font-bold text-gray-600 group-hover:text-brand transition-colors">Home</span>
          </Link>
        </div>

        {/* Hero header */}
        <section className="bg-[#FAF8F5] py-2 md:py-3">
          <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
            <div className="text-center mb-1">
              <span className="inline-block text-[10px] font-bold tracking-[0.2em] uppercase text-brand mb-1">
                Full Lineup
              </span>
              <h1 className="text-lg md:text-xl lg:text-2xl font-extrabold text-gray-900 mb-0.5">
                Every Airdog Model
              </h1>
              <p className="text-[10px] md:text-xs text-gray-500 font-semibold max-w-lg mx-auto leading-relaxed">
                Five models. One TPA technology. Find your perfect fit.
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3 mt-1 text-[10px] md:text-xs font-bold text-gray-500">
              <span className="flex items-center gap-1">
                <i className="ri-checkbox-circle-fill text-brand text-[10px]" /> Zero filters
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-checkbox-circle-fill text-brand text-[10px]" /> Washable plates
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-checkbox-circle-fill text-brand text-[10px]" /> 99.9% destruction
              </span>
            </div>
          </div>
        </section>

        {/* Models Grid */}
        <section className="py-2 md:py-3">
          <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-2">
              {models.map((m, i) => (
                <div
                  key={i}
                  className={`relative ${m.color} rounded-lg p-2 md:p-3 border ${m.border} hover:border-brand/30 hover:shadow-sm hover:shadow-brand/5 transition-all duration-300 cursor-pointer`}
                >
                  {/* Badge / Tag */}
                  {(m.badge || m.tag) && (
                    <span className={`absolute top-1.5 right-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-bold tracking-wider uppercase ${m.badge ? "bg-brand text-white" : "bg-gray-200 text-gray-700"}`}>
                      {m.badge || m.tag}
                    </span>
                  )}

                  {/* Icon */}
                  <div className={`w-6 h-6 rounded-md ${m.iconBg} flex items-center justify-center mb-1`}>
                    <i className={`${m.icon} ${m.iconColor} text-xs`} />
                  </div>

                  {/* Model name */}
                  <h2 className="text-sm md:text-base font-extrabold text-gray-900 mb-0.5">
                    {m.model}
                  </h2>

                  {/* Coverage */}
                  <div className="flex items-center gap-1 mb-0.5">
                    <i className="ri-ruler-line text-gray-400 text-[10px]" />
                    <span className="text-[10px] md:text-xs font-bold text-gray-800">{m.coverage}</span>
                  </div>

                  {/* Description */}
                  <p className="text-[10px] text-gray-600 font-semibold leading-snug line-clamp-2">
                    {m.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}